import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { permissoesDe } from './permissoes';
import Login from './Login';
import RegistarSessaoExterna from './RegistarSessaoExterna';
import AvaliacaoCliente from './AvaliacaoCliente';
import CommercialPage from './pages/CommercialPage';
import ConfirmarRecibo from './ConfirmarRecibo';
import FormularioCliente from './FormularioCliente';
import CondicoesFormacao from './CondicoesFormacao';
import FormadorFormacao from './FormadorFormacao';
import PortalCliente from './PortalCliente';
import AvaliarServico from './AvaliarServico';
import DashboardFisio from './pages/DashboardFisio';
import { novidadesPorVer } from './lib/novidades';
import PresencasPublica from './pages/PresencasPublica';

const C = {
  purple:"#691FB1", purpleLight:"#EDE7F6", white:"#FFFFFF",
  red:"#FF357B", border:"#E4DEFA", textLight:"#8888AA",
};

const ROLE_PERMISSIONS = {
  admin:      { label:"Administrador", canEditProjects:true,  canEditCalendar:true,  canViewFinancial:true,  canEditFinancial:true,  canManageUsers:true,  canSendPlanning:true, canViewCommercial:true,  canEditCommercial:true,  canViewAllCommercial:true },
  tl:         { label:"Team Leader",   canEditProjects:true,  canEditCalendar:true,  canViewFinancial:true,  canEditFinancial:false, canManageUsers:false, isTL:true, canSendPlanning:true, canViewCommercial:false, canEditCommercial:false },
  financial:  { label:"Financeiro",    canEditProjects:false, canEditCalendar:false, canViewFinancial:true,  canEditFinancial:true,  canManageUsers:false, canSendPlanning:false, canViewCommercial:true,  canEditCommercial:false, canViewAllCommercial:true },
  comercial:  { label:"Comercial",     canEditProjects:true,  canEditCalendar:false, canViewFinancial:true,  canEditFinancial:false, canManageUsers:false, canSendPlanning:false, canViewCommercial:true,  canEditCommercial:true,  canViewAllCommercial:false },
  operations: { label:"Operacional",   canEditProjects:true,  canEditCalendar:true,  canViewFinancial:true, canEditFinancial:false, financeOnlyProfessionals:true, canEditPaymentNotes:true, canManageUsers:false, canSendPlanning:true, canViewCommercial:false, canEditCommercial:false },
};

// Páginas carregadas a pedido. DEFINIDAS FORA DO COMPONENTE — se ficarem lá dentro,
// cada renderização cria componentes novos e o React desmonta e recarrega a página
// a cada passagem do rato no menu (ecrã branco + "A carregar...").
const KanbanPage    = React.lazy(() => import('./pages/KanbanPage'));
const TarefasPage   = React.lazy(() => import('./pages/TarefasPage'));
const ConversasPage = React.lazy(() => import('./pages/ConversasPage'));
const ReunioesPage = React.lazy(() => import('./pages/ReunioesPage'));
const AvaliacoesPage = React.lazy(() => import('./pages/AvaliacoesPage'));
const FormacoesPage = React.lazy(() => import('./pages/FormacoesPage'));
// Reuniões (atas com IA): em espera até aprovação — pôr true depois de correr reunioes.sql e configurar a chave da IA
const REUNIOES_ATIVO = true;
const ConversasAviso = React.lazy(() => import('./pages/ConversasAviso'));
const CalendarPage  = React.lazy(() => import('./pages/CalendarPage'));
const FinancialPage = React.lazy(() => import('./pages/FinancialPage'));
const UsersPage     = React.lazy(() => import('./pages/UsersPage'));
const SettingsPage  = React.lazy(() => import('./pages/SettingsPage'));
const ProfessionalsHub = React.lazy(() => import('./pages/ProfessionalsHub'));
const CheckinBar = React.lazy(() => import('./pages/CheckinBar'));
const PresencaPage = React.lazy(() => import('./pages/PresencaPage'));
const PremioConnectPage = React.lazy(() => import('./pages/PremioConnectPage'));
const BibliotecaPage = React.lazy(() => import('./pages/BibliotecaPage'));
const ContasPage = React.lazy(() => import('./pages/ContasPage'));
const TesourariaPage = React.lazy(() => import('./pages/TesourariaPage'));
const GalaEntradaPage = React.lazy(() => import('./pages/GalaEntradaPage'));
const GalaEuPage = React.lazy(() => import('./pages/GalaEuPage'));
const FaqPage = React.lazy(() => import('./pages/FaqPage'));
const RegistoAlteracoes = React.lazy(() => import('./pages/RegistoAlteracoes'));

// endereço de cada módulo do HUB (não pode coincidir com as páginas públicas: /formacao, /avaliacao, /presencas/…)
const CAMINHO_DA_PAGINA = { kanban:'projetos', calendar:'calendario', financial:'financeiro', commercial:'comercial', marketing:'marketing',
  users:'utilizadores', contas:'clientes', tesouraria:'tesouraria', settings:'definicoes', professionals:'rede', presenca:'pessoas',
  biblioteca:'biblioteca', faq:'faq', registo:'registo', tarefas:'tarefas', conversas:'conversas', reunioes:'reunioes',
  avaliacoes:'avaliacoes', formacoes:'formacoes', 'cfg-clientes':'configurar-clientes', 'cfg-profissionais':'contratos-profissionais',
  'cfg-atividades':'atividades', 'cfg-externos':'profissionais-externos' };
const PAGINA_DE_CAMINHO = Object.fromEntries(Object.entries(CAMINHO_DA_PAGINA).map(([k, v]) => [v, k]));

export default function App() {
  const [session,  setSession]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [veConversas, setVeConversas] = useState(false);   // acesso às Conversas do site (decidido na base)
  const [permExcecoes, setPermExcecoes] = useState({});   // ajustes individuais ao perfil
  const [userName, setUserName] = useState('');
  const [nNovidades, setNNovidades] = useState(0);   // novidades do HUB por ver (aparece no menu)
  // aviso flutuante das novidades (canto inferior direito): fechado nesta sessão até haver mais novidades
  const [novFechado, setNovFechado] = useState(() => { try { return Number(sessionStorage.getItem('novidadesFechadas') || 0); } catch (_) { return 0; } });
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [newPass, setNewPass] = useState('');
  const [recMsg, setRecMsg] = useState('');
  // Links diretos: ?page=marketing&projeto=<id> abre logo no sítio certo.
  // Serve para os emails de tarefas, notificações e partilha de links.
  const paramsURL = new URLSearchParams(window.location.search);
  const paginasValidas = ['kanban','calendar','financial','commercial','marketing','users','contas',
                          'tesouraria','settings','professionals','presenca','biblioteca','faq','registo','tarefas','conversas','reunioes','avaliacoes','formacoes',
                          'cfg-clientes','cfg-profissionais','cfg-atividades','cfg-externos'];
  const pageURL = paramsURL.get('page');
  // regresso da ligação Zoho (Conversas do site): guarda o resultado para a página o mostrar
  try { const z = paramsURL.get('zoho'); if (z) sessionStorage.setItem('zohoAviso', z); } catch (_) {}
  // Cada módulo tem o seu endereço (hub.workwell.pt/formacoes, /tesouraria…): o F5 mantém-se no sítio,
  // o «para trás» do browser volta ao módulo anterior e o link pode ser partilhado.
  // Os links antigos (?page=…) continuam a funcionar.
  const paginaDoCaminho = () => { const seg = decodeURIComponent(window.location.pathname.slice(1).split('/')[0] || ''); return PAGINA_DE_CAMINHO[seg] || null; };
  const [page,     setPage]     = useState(
    paramsURL.get('orc') ? 'commercial'
    : (pageURL && paginasValidas.includes(pageURL)) ? pageURL
    : paginaDoCaminho() || 'kanban');
  // id do projeto a abrir, quando vem no link
  // guardado no 1.º render: a barra é limpa logo a seguir e o módulo só abre depois do login
  const [projetoURL] = useState(() => paramsURL.get('projeto') || paramsURL.get('projeto_id') || null);
  const [acaoURL] = useState(() => paramsURL.get('acao'));   // ação de formação a abrir (link das tarefas e dos emails)
  const [abaURL]  = useState(() => paramsURL.get('aba'));
  const [showMenu, setShowMenu] = useState(false);
  // tira os parâmetros da barra depois de aplicados, para o F5 não voltar sempre ao mesmo sítio
  // Na 1.ª vez troca só o caminho e tira os parâmetros que o App já guardou (page, projeto, acao, aba);
  // os outros (orc, id, filtro, quem…) ficam, porque são os módulos que os leem quando abrem.
  const primeiraVez = React.useRef(true);
  React.useEffect(() => {
    // só nas páginas do HUB: as páginas públicas (/formacao, /avaliacao, /c/…) não se tocam
    const caminho = window.location.pathname;
    if (caminho !== '/' && !paginaDoCaminho()) return;
    const destino = '/' + (CAMINHO_DA_PAGINA[page] || page);
    // já está neste módulo (ex.: /projetos/<id>): o resto do caminho é do módulo, não se mexe
    const mesmoModulo = caminho === destino || caminho.startsWith(destino + '/');
    if (primeiraVez.current) {
      primeiraVez.current = false;
      const q = new URLSearchParams(window.location.search);
      ['page','projeto','projeto_id','acao','aba','zoho'].forEach(k => q.delete(k));
      const resto = q.toString();
      window.history.replaceState({ page }, '', (mesmoModulo ? caminho : destino) + (resto ? '?' + resto : '') + window.location.hash);   // o hash fica (link de recuperação da password)
      return;
    }
    if (!mesmoModulo) window.history.pushState({ page }, '', destino);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps
  // botões «para trás» / «para a frente» do browser
  React.useEffect(() => {
    const voltar = () => { const pg = paginaDoCaminho(); if (pg) setPage(pg); };
    window.addEventListener('popstate', voltar);
    return () => window.removeEventListener('popstate', voltar);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [moduloAberto, setModuloAberto] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) loadUserRole(session.user.email);
      else setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === 'PASSWORD_RECOVERY') { setRecoveryMode(true); setLoading(false); return; }
      setSession(session);
      if (session) loadUserRole(session.user.email);
      else { setUserRole(null); setLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  // novidades por ver: lê ao entrar e sempre que alguém dá um visto ou se publica uma novidade
  const emailSessao = session && session.user && session.user.email;
  useEffect(() => {
    if (!emailSessao || !userRole) return;
    const ler = () => novidadesPorVer(supabase, emailSessao, userRole).then(l => setNNovidades(l.length));
    ler();
    window.addEventListener('novidades-mudaram', ler);
    return () => window.removeEventListener('novidades-mudaram', ler);
  }, [emailSessao, userRole]);

  const loadUserRole = async (email) => {
    const { data } = await supabase.from('user_roles').select('role, full_name, permissoes').eq('email', email).single();
    supabase.rpc('salesiq_tenho_acesso').then(({ data: ok }) => setVeConversas(!!ok)).catch(() => setVeConversas(false));
    if (data) { setUserRole(data.role); setUserName(data.full_name); setPermExcecoes(data.permissoes || {}); }
    else { setUserRole('operations'); setUserName(email.split('@')[0]); setPermExcecoes({}); }
    setLoading(false);
  };

  const handleLogout = async () => { await supabase.auth.signOut(); setUserRole(null); setUserName(''); };

  // Rota publica do formulario de externos (sem login). Detetada pelo caminho do URL.
  if (window.location.pathname === '/registar-sessao') return <RegistarSessaoExterna />;
  if (window.location.pathname === '/avaliacao') return <AvaliacaoCliente />;
  if (window.location.pathname === '/avaliar') return <AvaliarServico />;
  if (window.location.pathname === '/gala/eu') {
    return (<React.Suspense fallback={<div style={{padding:40,textAlign:'center'}}>A carregar…</div>}>
      <GalaEuPage />
    </React.Suspense>);
  }
  if (window.location.pathname === '/confirmar-recibo') return <ConfirmarRecibo />;
  if (window.location.pathname === '/formulario-cliente') return <FormularioCliente />;
  if (window.location.pathname === '/formacao') return <CondicoesFormacao />;
  if (window.location.pathname === '/formacao-formador') return <FormadorFormacao />;
  if (window.location.pathname.startsWith('/c/')) return <PortalCliente />;
  if (window.location.pathname.startsWith('/fisio/')) return <DashboardFisio />;
  if (window.location.pathname.startsWith('/presencas/')) return <PresencasPublica supabase={supabase} token={window.location.pathname.split('/')[2]} logoSrc="/workwell-horizontal.png" />;

  // Perfil + ajustes individuais. As chaves antigas (canEditProjects, …)
  // continuam a existir para as páginas que já as usam.
  const perms = React.useMemo(() => {
    const base = ROLE_PERMISSIONS[userRole] || ROLE_PERMISSIONS.operations;
    const p = permissoesDe(userRole, permExcecoes);
    return {
      ...base,
      _mod: p,                                  // tabela nova, por módulo
      canEditProjects:    p.projetos.e,
      canEditCalendar:    p.calendario.e,
      canViewFinancial:   p.finClientes.v || p.finProfs.v || p.tesouraria.v,
      canEditFinancial:   p.finClientes.e || p.finProfs.e || p.tesouraria.e,
      canViewFinClientes: p.finClientes.v,
      canViewFinProfs:    p.finProfs.v,
      canViewTesouraria:  p.tesouraria.v,
      // quem só vê os profissionais não vê o Por Cliente nem a Tesouraria
      financeOnlyProfessionals: p.finProfs.v && !p.finClientes.v && !p.tesouraria.v,
      canManageUsers:     p.utilizadores.e,
      canViewCommercial:  p.comercial.v || p.comercial.p,
      canEditCommercial:  p.comissoes.e,
      canViewAllCommercial: p.comercial.v,
      canViewPresencasTodos: p.presencas.v,
      canEditPaymentNotes: p.finProfs.e,
      canEditBiblioteca:  p.biblioteca.e,
    };
  }, [userRole, permExcecoes]);

  if (loading) return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#F7F5FC', fontFamily:"'DM Sans',sans-serif", color:'#691FB1', fontSize:16 }}>A carregar...</div>;
  if (recoveryMode) {
    const definir = async () => {
      if (!newPass || newPass.length < 6) { setRecMsg('A password deve ter pelo menos 6 caracteres.'); return; }
      const { error } = await supabase.auth.updateUser({ password: newPass });
      if (error) { setRecMsg('Erro: ' + error.message); return; }
      setRecMsg('Password definida! A entrar...');
      setTimeout(() => { setRecoveryMode(false); window.location.href = window.location.origin; }, 1500);
    };
    return (
      <div style={{ minHeight:'100vh', background:'#F7F5FC', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'DM Sans',sans-serif" }}>
        <div style={{ background:'white', borderRadius:16, padding:40, width:380, boxShadow:'0 8px 40px rgba(105,31,177,0.12)' }}>
          <h1 style={{ margin:'0 0 8px', fontFamily:"'Fraunces',serif", color:'#691FB1', fontSize:22, textAlign:'center' }}>Definir nova password</h1>
          <p style={{ margin:'0 0 24px', color:'#8888AA', fontSize:13, textAlign:'center' }}>Escolhe a tua nova password de acesso.</p>
          <label style={{ fontSize:12, fontWeight:600, color:'#8888AA', display:'block', marginBottom:6 }}>Nova password</label>
          <input type="password" value={newPass} onChange={e=>setNewPass(e.target.value)} placeholder="••••••••"
            style={{ width:'100%', padding:'10px 12px', border:'1px solid #E4DEFA', borderRadius:8, fontSize:14, fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box', marginBottom:16 }}/>
          {recMsg && <div style={{ background:'#EDE7F6', borderRadius:8, padding:'10px 12px', fontSize:13, color:'#691FB1', marginBottom:16 }}>{recMsg}</div>}
          <button onClick={definir} style={{ width:'100%', padding:'12px', background:'#691FB1', border:'none', borderRadius:8, color:'white', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>Definir password</button>
        </div>
      </div>
    );
  }
  if (!session) return <Login />;

  // Gala Wellbeing Awards — check-in por QR. Requer login (a lista vive atras do RLS).
  if (window.location.pathname === '/gala' || window.location.pathname === '/gala/entrada') {
    return (
      <React.Suspense fallback={<div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#150E24', color:'#C6C3E7', fontFamily:'Helvetica,Arial,sans-serif', fontSize:15 }}>A carregar a entrada...</div>}>
        <GalaEntradaPage />
      </React.Suspense>
    );
  }



  // Navegação por módulos
  const MODULOS = [
    { id:'operacoes', icon:'⚙︎', label:'Operações', itens:[
      { id:'kanban',   icon:'⊞',  label:'Projetos',   desc:'Projetos, documentos e mapas de agendamento' },
      { id:'calendar', icon:'📅', label:'Calendário', desc:'Sessões agendadas e realizadas' },
      ...(veConversas ? [{ id:'conversas', icon:'💬', label:'Conversas do site', desc:'Chat do site (SalesIQ): receber e responder' }] : []),
      { id:'professionals', icon:'🧑‍⚕️', label:'Rede',  desc:'Profissionais externos e contratos' },
      { id:'formacoes', icon:'🎓', label:'Formações', desc:'Ações de formação, workshops e webinars: guia, dossier, contrato e faturação' },
      { id:'avaliacoes', icon:'⭐', label:'Avaliação dos serviços', desc:'Questionário dos participantes: resultados, comentários e links' },
      { id:'cfg-profissionais', icon:'👤', label:'Contratos & Faturas Profissionais', desc:'Dados, contratos e faturação dos profissionais' },
      { id:'cfg-atividades',    icon:'🎯', label:'Atividades',  desc:'Catálogo de atividades e serviços' },
      { id:'cfg-externos',      icon:'🔑', label:'Profissionais Externos', desc:'Códigos de acesso e permissões' },
    ]},
    { id:'contas', icon:'🏢', label:'Contas', itens:[
      { id:'contas',       icon:'🏢', label:'Clientes',           desc:'Ficha de cada cliente, projetos e faturação' },
      { id:'cfg-clientes', icon:'🗂️', label:'Configurar clientes', desc:'Criar e editar clientes' },
    ]},
    ...(perms.canViewFinancial ? [{ id:'fin', icon:'💶', label:'Financeiro', itens:[
      { id:'financial',   icon:'💶', label:'Financeiro', desc:'Apuramentos, faturação e pagamentos' },
      ...(perms.canEditFinancial ? [{ id:'tesouraria', icon:'📈', label:'Tesouraria', desc:'Fluxo de caixa: saldo real e projeção' }] : []),
    ]}] : []),
    ...(perms.canViewCommercial ? [{ id:'com', icon:'📋', label:'Comercial & Marketing', itens:[
      { id:'commercial', icon:'📋', label:'Comercial', desc:'Orçamentos, vendas, comissões e objetivos' },
      { id:'marketing',  icon:'📣', label:'Marketing', desc:'Campanhas e ações de marketing' },
    ]}] : []),
    { id:'tarefas-hub', icon:'✓', label:'Tarefas', itens:[
      { id:'tarefas', icon:'✓', label:'Tarefas', desc:'O que tens para fazer, de projetos, marketing ou avulso' },
      ...(REUNIOES_ATIVO ? [{ id:'reunioes', icon:'🗒', label:'Reuniões', desc:'Atas e tarefas a partir da transcrição das reuniões' }] : []),
    ]},
    { id:'pessoas', icon:'👥', label:'Pessoas', itens:[
      { id:'presenca',      icon:'📍', label:'Pessoas',  desc:'Presença, férias, banco de horas e gestão de pessoas' },
      ...(perms.canManageUsers ? [{ id:'users', icon:'🔑', label:'Utilizadores', desc:'Acessos ao HUB' }] : []),
      ...(userRole==='admin' ? [{ id:'registo', icon:'🕓', label:'Registo de alterações', desc:'Quem criou, alterou ou apagou o quê' }] : []),
    ]},
    { id:'recursos', icon:'📚', label: nNovidades ? `Recursos · ${nNovidades} 🆕` : 'Recursos', itens:[
      { id:'biblioteca', icon:'📚', label:'Biblioteca', desc:'Documentos e materiais internos' },
      { id:'faq',        icon:'❓', label: nNovidades ? `Perguntas frequentes · ${nNovidades} novidade${nNovidades > 1 ? 's' : ''} por ver` : 'Perguntas frequentes', desc:'Novidades do HUB, e onde fica e como se faz' },
    ]},
  ];
  const moduloDaPagina = (p) => MODULOS.find(m => m.itens.some(i => i.id === p));
  const paginaAtual = MODULOS.flatMap(m=>m.itens).find(i => i.id === page);

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif", minHeight:'100vh', background:'#F7F5FC' }}>

      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Fraunces:wght@700&display=swap" rel="stylesheet"/>
      <header style={{ background:C.purple, display:'flex', alignItems:'center', height:56, boxShadow:'0 2px 20px rgba(105,31,177,0.3)', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'0 18px', borderRight:'1px solid rgba(255,255,255,0.15)' }}>
          <span style={{ fontFamily:"'Fraunces',serif", color:'white', fontSize:16, fontWeight:700 }}><img src={process.env.PUBLIC_URL + '/logo192.png'} alt={''} style={{height:30,verticalAlign:'middle',marginRight:8}} />HUB Workwell</span>
        </div>
        <nav style={{ display:'flex', flex:1, padding:'0 6px', position:'relative' }}
          onMouseLeave={()=>setModuloAberto(null)}>
          {MODULOS.map(m => {
            const ativo = moduloDaPagina(page)?.id === m.id;
            const aberto = moduloAberto === m.id;
            return (
              <div key={m.id} style={{ position:'relative' }}
                onMouseEnter={()=>setModuloAberto(m.id)}>
                <button onClick={()=>setModuloAberto(a=>a===m.id?null:m.id)}
                  style={{ background:(ativo||aberto)?'rgba(255,255,255,0.18)':'transparent', border:'none', color:'white', padding:'0 16px', height:56, fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:ativo?700:500, display:'flex', alignItems:'center', gap:6, borderBottom:ativo?'2px solid rgba(255,255,255,0.85)':'2px solid transparent', opacity:ativo?1:0.85, whiteSpace:'nowrap' }}>
                  <span style={{ fontSize:14 }}>{m.icon}</span> {m.label}
                  <span style={{ fontSize:8, opacity:0.6, marginLeft:2 }}>▼</span>
                </button>
                {aberto && (
                  <div style={{ position:'absolute', left:6, top:'100%', background:C.white, borderRadius:12, border:`1px solid ${C.border}`, boxShadow:'0 10px 36px rgba(26,26,46,0.18)', zIndex:220, minWidth:260, overflow:'hidden', padding:'6px 0' }}>
                    {m.itens.map(i => (
                      <button key={i.id} onClick={()=>{ setPage(i.id); setModuloAberto(null); }}
                        style={{ width:'100%', display:'flex', alignItems:'flex-start', gap:10, padding:'10px 14px', border:'none', background:page===i.id?C.purpleLight:'transparent', cursor:'pointer', textAlign:'left', fontFamily:"'DM Sans',sans-serif" }}
                        onMouseEnter={e=>{ if(page!==i.id) e.currentTarget.style.background='#FAF8FF'; }}
                        onMouseLeave={e=>{ if(page!==i.id) e.currentTarget.style.background='transparent'; }}>
                        <span style={{ fontSize:16, lineHeight:'18px' }}>{i.icon}</span>
                        <span>
                          <span style={{ display:'block', fontSize:13, fontWeight:700, color:page===i.id?C.purple:'#1A1A2E' }}>{i.label}</span>
                          {i.desc && <span style={{ display:'block', fontSize:11, color:C.textLight, marginTop:1 }}>{i.desc}</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <span style={{ flex:1 }}/>
          {paginaAtual && (
            <span style={{ alignSelf:'center', color:'rgba(255,255,255,0.75)', fontSize:12, whiteSpace:'nowrap', paddingRight:10 }}>
              {moduloDaPagina(page)?.label} › <strong style={{ color:'#fff' }}>{paginaAtual.label}</strong>
            </span>
          )}
        </nav>
        <div style={{ padding:'0 14px', position:'relative', flexShrink:0 }}>
          <button onClick={() => setShowMenu(m=>!m)}
            style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, color:'white', padding:'4px 11px', fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontFamily:"'DM Sans',sans-serif" }}>
            <span style={{ width:22, height:22, borderRadius:'50%', background:C.red, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700 }}>{userName?userName[0].toUpperCase():'?'}</span>
            <span style={{ maxWidth:100, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{userName.split(' ')[0]}</span>
            <span style={{ fontSize:9, opacity:0.7 }}>▼</span>
          </button>
          {showMenu && (
            <div style={{ position:'absolute', right:0, top:'calc(100% + 4px)', background:C.white, borderRadius:10, border:`1px solid ${C.border}`, boxShadow:'0 8px 32px rgba(0,0,0,0.15)', zIndex:200, minWidth:210, overflow:'hidden' }}>
              <div style={{ padding:'10px 14px', borderBottom:`1px solid ${C.border}` }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#1A1A2E' }}>{userName}</div>
                <div style={{ fontSize:11, color:C.textLight, marginTop:2 }}>{ROLE_PERMISSIONS[userRole]?.label}</div>
                <div style={{ fontSize:11, color:C.textLight }}>{session.user.email}</div>
              </div>
              <button onClick={handleLogout} style={{ width:'100%', padding:'10px 14px', border:'none', background:'transparent', color:'#C0392B', fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", textAlign:'left' }}>Sair</button>
            </div>
          )}
        </div>
      </header>
      <main>
        <React.Suspense fallback={null}>
          <CheckinBar userEmail={session.user.email} userName={userName} userRole={userRole}/>
          {veConversas && <ConversasAviso paginaAtual={page} abrir={()=>setPage('conversas')}/>}
          {nNovidades > 0 && nNovidades > novFechado && page !== 'faq' && (
            <div role="status" style={{ position:'fixed', right:16, bottom:16, zIndex:1900, background:'#fff', borderRadius:14,
              boxShadow:'0 8px 28px rgba(26,26,46,0.18)', border:'1px solid #E4DEFA', borderTop:'4px solid #691FB1', padding:'14px 16px 14px 16px',
              width:300, maxWidth:'calc(100vw - 32px)', fontFamily:"'DM Sans',sans-serif" }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
                <span style={{ fontSize:18, lineHeight:1 }}>📣</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14.5, fontWeight:700, color:'#1A1A2E' }}>Há novidades no HUB!</div>
                  <div style={{ fontSize:12.5, color:'#4A4A6A', marginTop:2 }}>{nNovidades === 1 ? '1 novidade por ver' : `${nNovidades} novidades por ver`}</div>
                </div>
                <span title="Fechar" onClick={()=>{ setNovFechado(nNovidades); try { sessionStorage.setItem('novidadesFechadas', String(nNovidades)); } catch (_) {} }}
                  style={{ cursor:'pointer', color:'#8888AA', fontSize:16, lineHeight:1, padding:'0 2px' }}>✕</span>
              </div>
              <button onClick={()=>setPage('faq')} style={{ marginTop:10, background:'#691FB1', color:'#fff', border:'none', borderRadius:8,
                padding:'8px 14px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>Ver o que mudou</button>
            </div>)}
        </React.Suspense>
        <React.Suspense fallback={<div style={{ padding:60, textAlign:'center', color:C.purple, fontFamily:"'DM Sans',sans-serif", fontSize:14 }}>A carregar...</div>}>
          {page==='kanban'   && <KanbanPage    perms={perms} userName={userName} userEmail={session.user.email} projetoInicial={projetoURL}/>}
          {page==='calendar' && <CalendarPage  perms={perms}/>}
          {page==='financial'&& perms.canViewFinancial && <FinancialPage perms={perms} userName={userName} userEmail={session.user.email}/>}
          {(page==='commercial'||page==='marketing') && perms.canViewCommercial && <CommercialPage perms={perms} userName={userName} userEmail={session.user.email} vistaInicial={page==='marketing'?'marketing':'list'} projetoInicial={projetoURL}/>}
          {page==='users'    && perms.canManageUsers   && <UsersPage/>}
          {page==='contas'   && <ContasPage perms={perms} userName={userName} userEmail={session.user.email}/>}
          {page==='tesouraria' && perms.canEditFinancial && <TesourariaPage perms={perms} userName={userName} userEmail={session.user.email}/>}
          {page==='settings' && <SettingsPage perms={perms} userEmail={session.user.email}/>}
          {page==='cfg-clientes'      && <SettingsPage perms={perms} tabInicial="clients" vistaUnica userEmail={session.user.email}/>}
          {page==='cfg-profissionais' && <SettingsPage perms={perms} tabInicial="professionals" vistaUnica userEmail={session.user.email}/>}
          {page==='cfg-atividades'    && <SettingsPage perms={perms} tabInicial="activities" vistaUnica userEmail={session.user.email}/>}
          {page==='cfg-externos'      && <SettingsPage perms={perms} tabInicial="externos" vistaUnica userEmail={session.user.email}/>}
              {page==='professionals' && <ProfessionalsHub perms={perms}/>}
          {page==='presenca' && <PresencaPage userEmail={session.user.email} isAdmin={userRole==='admin'} mod={perms._mod}/>}
          {page==='tarefas'  && <TarefasPage   perms={perms} userRole={userRole} userEmail={session.user.email} userName={userName}/>}
          {page==='formacoes' && <FormacoesPage userEmail={session.user.email} userName={userName} userRole={userRole} acaoInicial={acaoURL} abaInicial={abaURL}/>}
          {page==='avaliacoes' && <AvaliacoesPage userEmail={session.user.email} userRole={userRole}/>}
          {page==='reunioes' && REUNIOES_ATIVO && <ReunioesPage userEmail={session.user.email} userName={userName} userRole={userRole}/>}
          {page==='conversas' && veConversas && <ConversasPage userEmail={session.user.email} userName={userName} userRole={userRole}/>}
          {page==='biblioteca' && <BibliotecaPage perms={perms} isAdmin={userRole==='admin'}/>}
          {page==='registo'    && userRole==='admin' && <div style={{ padding:'24px 28px' }}><RegistoAlteracoes supabase={supabase}/></div>}
          {page==='faq'        &&<div style={{ padding:'24px 28px' }}><FaqPage supabase={supabase}/></div>}
        </React.Suspense>
      </main>
    </div>
  );
}